import { useEffect, useRef, useState } from 'react';

interface UseRevealOptions {
  /** Margem do viewport pra disparar o reveal antes do elemento bater a borda visivel. Default: '0px 0px -10% 0px' (dispara 10% antes do topo). */
  readonly rootMargin?: string;
  /** Fracao do elemento que precisa estar visivel pra disparar. Default: 0.15 (15%). */
  readonly threshold?: number;
  /** Se true, reverte para escondido quando elemento sai do viewport. Default: false (reveal e definitivo — mais agradavel, evita "piscar" em scrolls rapidos). */
  readonly reRevealOnExit?: boolean;
}

interface UseRevealResult<T extends HTMLElement> {
  readonly ref: React.RefObject<T>;
  readonly isRevealed: boolean;
}

/**
 * Hook de scroll-reveal CSS-only (UTIL-02 — alternativa a framer-motion).
 *
 * Usa `IntersectionObserver` (zero dep) pra observar quando um elemento entra
 * no viewport e flipar um boolean. Consumer aplica classes condicionais
 * (`opacity-0 translate-y-6` → `opacity-100 translate-y-0`) com Tailwind +
 * `transition-all` pra animar o reveal.
 *
 * Por que nao framer-motion: PRD §7.4 + tabela "Out of Scope" probem libs de
 * animacao pesadas (Framer Motion sozinho pesa ~45KB gzipped — torra 9% do
 * budget 500KB). Esse hook custa ~200 bytes minificado e cobre 80% dos casos
 * de reveal em landing institucional (fade + slide on enter).
 *
 * Comportamento padrao (`reRevealOnExit: false`): uma vez revelado, o
 * elemento permanece visivel. Isso evita animacao em loop quando o usuario
 * sobe/desce a pagina rapidamente — sentido na pratica que "vivifica" o
 * scroll sem virar Christmas-tree visual.
 *
 * Acessibilidade:
 * - Reveal e PURAMENTE VISUAL — o conteudo esta sempre no DOM e acessivel a
 *   leitores de tela desde o primeiro paint. O `IntersectionObserver` so
 *   ativa uma classe CSS.
 * - `prefers-reduced-motion: reduce` e respeitado pelo consumer (a
 *   `transition-all` da Tailwind ja desliga em `motion-reduce:`); este hook
 *   nao precisa saber disso.
 *
 * Reuso multi-purpose: o mesmo IntersectionObserver pattern serve para
 * lazy-loading futuro (Phase 6) — basta ler `isRevealed` para decidir
 * importar componentes pesados dinamicamente.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseRevealOptions = {},
): UseRevealResult<T> {
  const { rootMargin = '0px 0px -10% 0px', threshold = 0.15, reRevealOnExit = false } = options;
  const ref = useRef<T>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (node === null) return undefined;

    // IntersectionObserver callback sempre recebe array nao-vazio (uma entry por
    // elemento observado). Como observamos so um node por hook, entries[0] e
    // sempre defined em runtime — TS confirma o tipo nao-undefined sem narrow.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          if (!reRevealOnExit) {
            observer.disconnect();
          }
        } else if (reRevealOnExit) {
          setIsRevealed(false);
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(node);
    return (): void => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, reRevealOnExit]);

  return { ref, isRevealed };
}
